/* eslint relay/unused-fields: "off" */

import { useRef, Suspense, useState, lazy, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { connect } from 'react-redux';

import * as tracking from 'dibs-tracking';
import { LABEL_VALUE_MONITORING_GROUP_PDP } from 'dibs-tracking/src/prometheus/peformanceTracking';
import classNames from 'classnames';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import scrollIntoView from 'scroll-into-view';
import { trackAbTestV2Variant } from 'dibs-buyer-layout/exports/clientABTestV2';

// components
import { PdpRespPreviewMessageBanner } from '../PdpRespPreviewMessageBanner/PdpRespPreviewMessageBanner';
import { PdpRespPrintHeader } from '../PdpRespPrintHeader/PdpRespPrintHeader';
import { PdpRespSimilarItemsLanding } from '../PdpRespSimilarItems/PdpRespSimilarItemsLanding';
import { PdpRespBreadcrumbs } from '../PdpRespBreadcrumbs/PdpRespBreadcrumbs';
import { PdpRespTitle } from '../PdpRespTitle/PdpRespTitle';
import { PdpSharedPrivateOfferBanner } from '../PdpSharedPrivateOfferBanner/PdpSharedPrivateOfferBanner';
import { PdpSharedSidebarFavorites } from '../shared/PdpSharedSidebarFavorites/PdpSharedSidebarFavorites';
import { PriceDisplay } from 'dibs-buyer-price';
import { PdpRespSidebarButtons } from '../PdpRespSidebarButtons/PdpRespSidebarButtons';
import { PdpRespSidebarBlurbs } from '../PdpRespSidebarBlurbs/PdpRespSidebarBlurbs';
import { PdpRespDetailTabs, useDetailTabsState } from '../PdpRespDetailTabs/PdpRespDetailTabs';
import { BUYER_INCENTIVES } from '../helpers/respTabsConstants';
import { PdpRespSidebarPillsLazy } from '../PdpRespSidebarPills/PdpRespSidebarPillsLazy';
import { PdpRespYMALItemsCarousel } from '../PdpRespProductTileCarousels/PdpRespYMALItemsCarousel';
import { PdpRespPersonalizedItemsCarouselLazy } from '../PdpRespProductTileCarousels/PdpRespPersonalizedItemsCarouselLazy';
import { PdpRespSimilarItemsCarousel } from '../PdpRespProductTileCarousels/PdpRespSimilarItemsCarousel';
import { PdpSharedQuantitySelect } from '../PdpSharedButtons/PdpSharedQuantitySelect';
import { PdpSharedReminderLoader } from '../PdpSharedReminder/PdpSharedReminderLoader';
import { PdpSharedRecentHistoryTrackerDeprecated } from '../shared/PdpSharedRecentHistoryTracker/PdpSharedRecentHistoryTrackerDeprecated';
import { PdpSharedPromoCode } from '../PdpSharedPromoCode/PdpSharedPromoCode';
import { PdpSharedRestrictedMaterialsLoader } from '../PdpSharedRestrictedMaterials/PdpSharedRestrictedMaterialsLoader';
import { PdpRespSwatchSelector } from '../PdpRespSwatchSelector/PdpRespSwatchSelector';
import { PdpSharedFramingStatusLazy } from '../PdpSharedFramingStatus/PdpSharedFramingStatusLazy';
import { PdpRespDealerChatLoader } from '../PdpRespDealerChat/PdpRespDealerChatLoader';
import { SharedRespLinksBlock } from '../../shared/SharedRespLinksBlock/SharedRespLinksBlock';
import { PdpSharedEditorialArticles } from '../PdpSharedEditorialArticles/PdpSharedEditorialArticles';
import { PdpSharedAuctionInfo } from '../PdpSharedAuctionInfo/PdpSharedAuctionInfo';
import { RecentlyViewedItemsLazy } from 'dibs-recent-history/exports/RecentlyViewedItemsLazy';
import { PdpRespSidebarSellerInfoAndContactLazy } from '../PdpRespSidebarSellerInfo/PdpRespSidebarSellerInfoAndContactLazy';
import { PdpRespSimilarAuctionItemsLazy } from '../PdpRespSimilarAuctionItems/PdpRespSimilarAuctionItemsLazy';
import { PdpSharedAuctionCountdownLazy } from '../PdpSharedAuctionCountdown/PdpSharedAuctionCountdownLazy';
import { PdpRespSellerItemsCarousel } from '../PdpRespProductTileCarousels/PdpRespSellerItemsCarousel';
import { PdpRespSharedHeroCarousel } from '../respShared/PdpRespSharedHeroCarousel/PdpRespSharedHeroCarousel';
import { PdpRespIconBubblesWrapper } from '../PdpRespIconBubbles/PdpRespIconBubblesWrapper';
import { PdpRespSharedPrintDetailsLazy } from '../respShared/PdpRespSharedPrintDetails/PdpRespSharedPrintDetailsLazy';
import { PdpRespSharedQrCodeLazy } from '../respShared/PdpRespSharedQrCode/PdpRespSharedQrCodeLazy';
import { PdpSharedUrgencyLazy } from '../PdpSharedUrgency/PdpSharedUrgencyLazy';
import { SharedPerformanceTracker } from '../../shared/SharedPerformanceTracker';

const MerchandizingTool = lazy(() =>
    import(
        /* webpackChunkName: "MerchandizingTool" */ 'dibs-merchandizing-tools/exports/MerchandizingTool'
    )
);

const PdpSharedOptInPromptModal = lazy(() =>
    import(
        /* webpackChunkName: "PdpSharedOptInPromptModal" */ '../PdpSharedOptInPromptModal/PdpSharedOptInPromptModal'
    )
);

// callouts
import { NOTIFICATION_KEYS as SHARED_NOTIFICATION_KEYS } from '../PdpSharedNotifications/pdpSharedNotificationConstants';
import { SharedNotificationsWrapper } from '../../shared/SharedNotificationsWrapper';
import { PdpSharedNotificationMultiplePdpVisitsModalLoader } from '../PdpSharedNotifications/PdpSharedNotificationMultiplePdpVisitsModal/PdpSharedNotificationMultiplePdpVisitsModalLoader';
import { getSelectedSku } from '../helpers/getSelectedSku';
import { SELLER_ITEMS_LIST } from '../tracking/similarItemsTracking';
import { trackPdpPriceRoundingAbTestVariant } from 'dibs-buyer-price/exports/priceDisplayRounding';

// helpers
import classnames from 'classnames';
import {
    getIsOptinPromptShown,
    getSessionPdpItemSaved,
    setSessionPdpItemSaved,
} from '../PdpSharedOptInPromptModal/pdpSharedOptInPromptModaHelpers';
import {
    usePdpUrgencyPlacementClientVariant,
    trackPdpUrgencyPlacementAbTestVariant,
} from '../helpers/pdpUrgencyPlacementHelpers';

// context
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

// styles
import styles from './PdpRespContainer.scss';
import dibsCss from 'dibs-css';

/* eslint-disable react/prop-types */
// disable for function as child of PdpRespNotificationsWrapper

export function PdpRespContainerComponent(props) {
    const {
        printWithPrice,
        currency,
        viewer,
        user,
        isPdpRespPreviewPage,
        item,
        displayInfo,
        similarLandingItems,
        relayVariables,
        relay,
        selectedSkuId,
        onSkuChange,
        itemSearch,
    } = props;
    const { isMultiSku, itemId, userId, isClient, isTrade, fetchUser, fetchAuction } =
        relayVariables;
    const selectedSku = getSelectedSku(item, selectedSkuId);
    const { seller, isAuctionEnabled, itemIsOnHold } = item || {};
    const isSellerAccessible = seller?.isAccessible;
    const showAcceptOffer = displayInfo?.ctas?.showAcceptOffer;
    const [itemQuantity, setItemQuantity] = useState(null);
    const [showOptinPrompt, setShowOptinPrompt] = useState(false);

    const { pdpOptinPromptFF, buyerUrgencyInfo } = viewer || {};

    const { isTablet } = useServerVarsContext();

    const isPdpUrgencyPlacementClientVariant = usePdpUrgencyPlacementClientVariant();
    const hasUrgencyInfo = buyerUrgencyInfo?.itemUrgency?.hasUrgencyInfo;

    const scrollToRef = useRef(null);
    const detailsSectionRef = useRef(null);
    const scrollToCustomizationRef = useRef(null);

    const includePromoCode = isSellerAccessible && !itemIsOnHold;

    const hasEditorialArticles =
        !!item?.storyTellingModule?.tabs?.[0]?.supplementaryEditorial?.articles.length;

    const roundPriceTrackingTriggered = useRef(false);

    const handleRoundPriceTracking = () => {
        if (!roundPriceTrackingTriggered.current) {
            trackPdpPriceRoundingAbTestVariant();
            roundPriceTrackingTriggered.current = true;
        }
    };

    const onFavorited = useCallback(() => {
        if (!pdpOptinPromptFF) {
            return;
        }
        const isOptinPromptShown = getIsOptinPromptShown();
        const isPdpItemSaved = getSessionPdpItemSaved();
        const shouldShowOptinPrompt = !!userId && !isOptinPromptShown && !isPdpItemSaved;

        if (shouldShowOptinPrompt) {
            setSessionPdpItemSaved();
            setShowOptinPrompt(true);
        }
    }, [userId, pdpOptinPromptFF]);

    const detailTabsState = useDetailTabsState();
    const buyerIncentivesSidebarClick = useCallback(
        event => {
            const detailsSection = detailsSectionRef?.current;
            if (detailsSection) {
                scrollIntoView(detailsSection, { time: 500 }, () => detailsSection.focus());
            }

            const [, setActiveTab] = detailTabsState;
            setActiveTab(BUYER_INCENTIVES);

            tracking.trackEvent(
                {
                    category: 'product',
                    action: 'Learn More click',
                    label: 'The 1stDibs Promise - Siderail',
                },
                event
            );
        },
        [detailTabsState]
    );

    useEffect(() => {
        if (!isAuctionEnabled && hasUrgencyInfo) {
            trackPdpUrgencyPlacementAbTestVariant();
        }
    }, [isAuctionEnabled, hasUrgencyInfo]);

    useEffect(() => {
        trackAbTestV2Variant('ReturnsonPDP');
    }, []);

    return (
        <div>
            {/* Preview Page Message Banner */}
            {isPdpRespPreviewPage && <PdpRespPreviewMessageBanner />}

            {/* Print Header */}
            {isClient && <PdpRespPrintHeader itemId={itemId} className={styles.printShow} />}

            {/* Similar Items */}
            <div className={styles.printHide}>
                <PdpRespSimilarItemsLanding
                    scrollToRef={scrollToRef}
                    item={item}
                    similarItems={similarLandingItems}
                    viewer={viewer}
                    user={user}
                    currency={currency}
                />
            </div>

            {/* Notification reminder loader */}
            <PdpSharedReminderLoader
                relayVariables={{
                    userId,
                    fetchUser,
                    itemId,
                    isClient,
                }}
                load={!!user}
            />

            {/* PdpBreadcrumbs */}
            <div className={styles.printHide}>
                <PdpRespBreadcrumbs item={item} />
            </div>

            <div
                className={classnames(
                    'rowFlex',
                    dibsCss.relative /* position: relative required for MerchandizingTool */
                )}
                data-tn="pdp-main-container"
            >
                <div
                    className={classnames(
                        styles.topContainer,
                        styles.carouselContainer,
                        'colXs12 colLg7 colXl8'
                    )}
                >
                    {/* Image Carousel */}
                    <PdpRespSharedHeroCarousel
                        viewer={viewer}
                        item={item}
                        user={user}
                        currency={currency}
                        afterDisplay={isClient}
                        selectedSkuId={selectedSkuId}
                        size={isTablet ? 'medium' : 'large'}
                        disableModals={isPdpRespPreviewPage}
                        displayItemUrgency={isPdpUrgencyPlacementClientVariant}
                    >
                        <PdpRespIconBubblesWrapper
                            viewer={viewer}
                            item={item}
                            user={user}
                            relayVariables={{
                                itemId,
                                isTrade,
                            }}
                            skuId={selectedSkuId}
                            dataTn="image-carousel-icons"
                        />
                    </PdpRespSharedHeroCarousel>
                </div>

                {/* Sidebar */}
                <div
                    className={classnames(
                        styles.topContainer,
                        styles.sidebarContainer,
                        'colXs12 colLg5 colXl4'
                    )}
                >
                    {isAuctionEnabled && (
                        <div className={styles.printHide}>
                            <PdpSharedAuctionCountdownLazy item={item} />
                        </div>
                    )}

                    <div className="rowFlex">
                        {/* Title */}
                        <div className="colXs12 colLg10">
                            {isClient && !isPdpUrgencyPlacementClientVariant && hasUrgencyInfo && (
                                <PdpSharedUrgencyLazy buyerUrgencyInfo={buyerUrgencyInfo} />
                            )}
                            {isClient &&
                                ((!isPdpUrgencyPlacementClientVariant && !hasUrgencyInfo) ||
                                    isPdpUrgencyPlacementClientVariant) && (
                                    <PdpRespSidebarPillsLazy
                                        item={item}
                                        scrollToCustomizationRef={scrollToCustomizationRef}
                                    />
                                )}
                            <PdpRespTitle item={item} />
                            {isClient && showAcceptOffer && <PdpSharedPrivateOfferBanner />}
                            {!isAuctionEnabled && (
                                <div
                                    className={classnames({
                                        [styles.printHide]: !printWithPrice,
                                    })}
                                >
                                    {isClient && (
                                        <>
                                            <SharedPerformanceTracker
                                                datadogTimer={{ name: 'pdp_time_to_price' }}
                                                measureFromPageInit={{
                                                    subGroup: 'resp/price-box',
                                                    monitoringGroup:
                                                        LABEL_VALUE_MONITORING_GROUP_PDP,
                                                }}
                                            />
                                            <PriceDisplay
                                                currency={currency}
                                                item={item}
                                                useSvgIcon
                                                sku={isMultiSku ? selectedSku : null}
                                                appendElementToPrice={
                                                    <PdpSharedFramingStatusLazy item={item} />
                                                }
                                                displayVatLabel
                                                roundPrice
                                                handleRoundPriceTracking={handleRoundPriceTracking}
                                            />
                                        </>
                                    )}

                                    {includePromoCode && (
                                        <div className={styles.printHide}>
                                            <PdpSharedPromoCode
                                                displayInfo={displayInfo}
                                                itemId={itemId}
                                                viewer={viewer}
                                                user={user}
                                                userId={userId}
                                                currency={currency}
                                                excludeShippingPromos
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {/* Favorite / Folder */}
                        <div
                            className={classNames(
                                styles.printHide,
                                styles.favoritesContainer,
                                'colXs2'
                            )}
                        >
                            <PdpSharedSidebarFavorites
                                environment={relay.environment}
                                item={item}
                                showHeartCount
                                heartIconWrapperClass={styles.heartIconWrapper}
                                itemImage={item.firstPhotoWebPath || ''}
                                isClient={isClient}
                                userId={userId}
                                wrapperClass={styles.favoritesWrapper}
                                onFavorited={onFavorited}
                            />
                        </div>
                    </div>

                    {!isAuctionEnabled && isMultiSku && (
                        <PdpRespSwatchSelector
                            currency={currency}
                            isClient={isClient}
                            item={item}
                            user={user}
                            selectedSkuId={selectedSkuId}
                            onClick={skuId => {
                                onSkuChange(skuId);
                            }}
                            showSwatchDescriptionOnHover
                        />
                    )}

                    {fetchAuction && (
                        <>
                            <div
                                className={classnames({
                                    [styles.printHide]: !printWithPrice,
                                })}
                            >
                                <PdpSharedAuctionInfo
                                    viewer={viewer}
                                    user={user}
                                    item={item}
                                    currency={currency}
                                    handleRoundPriceTracking={handleRoundPriceTracking}
                                />
                            </div>
                            {isSellerAccessible && (
                                <div className={styles.printHide}>
                                    <PdpSharedPromoCode
                                        displayInfo={displayInfo}
                                        viewer={viewer}
                                        itemId={itemId}
                                        user={user}
                                        userId={userId}
                                        currency={currency}
                                        excludeShippingPromos
                                    />
                                </div>
                            )}
                        </>
                    )}

                    {/* Item Ctas */}
                    {isClient && !isAuctionEnabled && (
                        <div className={`${styles.printHide} ${styles.itemCtasWrapper}`}>
                            <PdpSharedQuantitySelect
                                item={item}
                                selectedSkuId={selectedSkuId}
                                setItemQuantity={setItemQuantity}
                            >
                                {quantitySelected => (
                                    <PdpRespSidebarButtons
                                        relayVariables={{
                                            userId,
                                            isMultiSku,
                                            itemId,
                                        }}
                                        user={user}
                                        item={item}
                                        displayInfo={displayInfo}
                                        quantitySelected={quantitySelected}
                                        skuId={selectedSkuId}
                                        itemSearch={itemSearch}
                                        viewer={viewer}
                                    />
                                )}
                            </PdpSharedQuantitySelect>
                        </div>
                    )}

                    {/* Restricted Materials */}
                    {isClient && <PdpSharedRestrictedMaterialsLoader viewer={viewer} />}

                    {/* Blurbs */}
                    {isSellerAccessible && (
                        <PdpRespSidebarBlurbs
                            currency={currency}
                            viewer={viewer}
                            item={item}
                            user={user}
                            displayInfo={displayInfo}
                            sku={selectedSku || null}
                            itemQuantity={itemQuantity}
                            isClient={isClient}
                            buyerIncentivesSidebarClick={buyerIncentivesSidebarClick}
                            handleRoundPriceTracking={handleRoundPriceTracking}
                        />
                    )}

                    {isClient && isSellerAccessible && (
                        <div className={classnames(styles.printHide, styles.sidebarSection)}>
                            {/* Questions about this piece? / Contact Dealer */}
                            <PdpRespSidebarSellerInfoAndContactLazy
                                seller={item.seller}
                                user={user}
                                item={item}
                                selectedSkuId={selectedSkuId}
                                itemQuantity={itemQuantity}
                                currency={currency}
                            />
                        </div>
                    )}
                    <PdpRespSharedQrCodeLazy itemId={itemId} className={styles.printShow} />
                </div>
                {user?.hasMerchandizingPermission && (
                    <Suspense fallback={null}>
                        <div data-dibs-merchandizing-tool-item={itemId} />
                        <MerchandizingTool itemIds={[itemId]} userId={userId} placement="pdp" />
                    </Suspense>
                )}
            </div>

            <div ref={scrollToCustomizationRef} />

            <div ref={detailsSectionRef} className={classnames(styles.details, styles.printHide)}>
                <PdpRespDetailTabs
                    viewer={viewer}
                    item={item}
                    user={user}
                    selectedSkuId={selectedSkuId}
                    itemQuantity={itemQuantity}
                    tabsState={detailTabsState}
                    currency={currency}
                />
                {/* Pdp Dealer Chat */}
                {isClient && <PdpRespDealerChatLoader viewer={viewer} currency={currency} />}
            </div>

            {/* Contact Dealer Details */}
            <div className={styles.printHide}>
                {/* Similar Items at Auction */}
                {fetchAuction && (
                    <div className={styles.auctionItems}>
                        <PdpRespSimilarAuctionItemsLazy
                            user={user}
                            viewer={viewer}
                            currency={currency}
                        />
                    </div>
                )}

                <PdpRespSellerItemsCarousel
                    user={user}
                    item={item}
                    viewer={viewer}
                    currency={currency}
                    trackingLocation={SELLER_ITEMS_LIST}
                />
            </div>

            {/* Print View */}
            <div className={classnames(styles.details, styles.printShow)}>
                <PdpRespSharedPrintDetailsLazy
                    item={item}
                    user={user}
                    currency={currency}
                    selectedSkuId={selectedSkuId}
                />
            </div>

            {/* You May Also Like */}
            <div ref={scrollToRef} />
            <div className={styles.printHide}>
                {/* People Also Browsed */}
                {!isAuctionEnabled && (
                    <PdpRespPersonalizedItemsCarouselLazy
                        viewer={viewer}
                        user={user}
                        currency={currency}
                        isClient={isClient}
                    />
                )}
                <PdpRespYMALItemsCarousel
                    currency={currency}
                    viewer={viewer}
                    user={user}
                    item={item}
                />
                <PdpRespSimilarItemsCarousel
                    viewer={viewer}
                    user={user}
                    item={item}
                    currency={currency}
                />
            </div>
            <RecentlyViewedItemsLazy
                itemsPerPage={5}
                wrapperClassName={classnames(styles.printHide, styles.recentlyViewed, {
                    [styles.divider]: hasEditorialArticles && viewer.isWordpressArticlesEnabled,
                })}
                authModalLoader={authModalLoader}
                currency={currency}
            />

            {viewer.isWordpressArticlesEnabled && (
                <>
                    <div className={styles.printHide}>
                        <PdpSharedEditorialArticles item={item} />
                    </div>
                </>
            )}

            {/* Photos - todo (INTL-70: PDP follow on) */}
            <SharedNotificationsWrapper type={SHARED_NOTIFICATION_KEYS.MULTI_PDP_VISITS}>
                {({ isEnabled }) => (
                    <PdpSharedNotificationMultiplePdpVisitsModalLoader
                        isEnabled={isEnabled}
                        itemId={itemId}
                    />
                )}
            </SharedNotificationsWrapper>

            {/* Optin pdp prompt modal */}
            {showOptinPrompt && (
                <Suspense fallback="">
                    <PdpSharedOptInPromptModal
                        isAuctionEnabled={isAuctionEnabled}
                        onModalClose={() => setShowOptinPrompt(false)}
                    />
                </Suspense>
            )}

            {/* Recent History */}
            {!isPdpRespPreviewPage && <PdpSharedRecentHistoryTrackerDeprecated item={item} />}

            {/* More Ways To Browse */}
            <div className={styles.printHide}>
                <SharedRespLinksBlock
                    viewer={viewer}
                    item={item}
                    links={item.internalBuyLinks}
                    itemSearch={null}
                    onClick={() => {
                        tracking.trackEvent({
                            category: 'product',
                            action: 'more ways to browse link click',
                        });
                    }}
                />
            </div>
        </div>
    );
}

PdpRespContainerComponent.propTypes = {
    currency: PropTypes.string,
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        itemId: PropTypes.string.isRequired,
        isClient: PropTypes.bool.isRequired,
        isMultiSku: PropTypes.bool.isRequired,
        isTrade: PropTypes.bool,
        fetchUser: PropTypes.bool.isRequired,
        followSearchPages: PropTypes.array,
        skuId: PropTypes.string,
    }).isRequired,
    onSkuChange: PropTypes.func.isRequired,
    relay: PropTypes.object.isRequired,
    selectedSkuId: PropTypes.string,

    // relay response data
    viewer: PropTypes.object.isRequired,
    itemSearch: PropTypes.object,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    displayInfo: PropTypes.object,
    similarLandingItems: PropTypes.object,

    // from redux
    printWithPrice: PropTypes.bool.isRequired,
    openTradeAskSellerTooltip: PropTypes.func,
};

function mapStateToProps({ pdpPrint, pdpPreviewPage, relayVariables }) {
    return {
        printWithPrice: pdpPrint.printWithPrice,
        isPdpRespPreviewPage: pdpPreviewPage.isPdpRespPreviewPage,
        relayVariables: relayVariables?.variables,
    };
}

export const PdpRespContainer = createFragmentContainer(
    connect(mapStateToProps)(PdpRespContainerComponent),
    {
        viewer: graphql`
            fragment PdpRespContainer_viewer on Viewer {
                isWordpressArticlesEnabled: isEnabledByLocale(featureName: "WP_EDITORIAL")
                pdpOptinPromptFF: featureFlag(feature: "pdpOptinPrompt")
                ...SharedRespLinksBlock_viewer
                ...PdpRespSidebarBlurbs_viewer
                ...PdpRespYMALItemsCarousel_viewer @include(if: $fetchPdp)
                ...PdpRespPersonalizedItemsCarouselLazy_viewer @skip(if: $fetchAuction)
                ...PdpRespSimilarItemsCarousel_viewer
                ...PdpRespSellerItemsCarousel_viewer
                    @arguments(priceBookName: $priceBookName)
                    @include(if: $fetchPdp)
                ...PdpRespSharedHeroCarousel_viewer
                    @include(if: $fetchPdp)
                    @arguments(fetchBuyerUrgencyInfo: $isClient)
                ...PdpRespIconBubblesWrapper_viewer @include(if: $fetchPdp)
                ...PdpRespDealerChatLoader_viewer @include(if: $isClient)
                ...PdpSharedRestrictedMaterialsLoader_viewer
                ...PdpSharedPromoCode_viewer
                ...PdpRespSimilarAuctionItemsLazy_viewer @include(if: $fetchAuction)
                ...PdpRespDetailTabs_viewer
                ...PdpRespSidebarButtons_viewer
                ...PdpSharedAuctionInfo_viewer @include(if: $fetchAuction)
                ... @include(if: $isClient) {
                    buyerUrgencyInfo(itemId: $itemId, userId: $userId) {
                        ...PdpSharedUrgencyLazy_buyerUrgencyInfo
                        itemUrgency {
                            hasUrgencyInfo
                        }
                    }
                }
            }
        `,
        user: graphql`
            fragment PdpRespContainer_user on User {
                hasMerchandizingPermission: hasPermission(name: "BUYER_FE_MERCH_TOOL")
                ...PdpRespYMALItemsCarousel_user
                ...PdpRespPersonalizedItemsCarouselLazy_user
                ...PdpRespSimilarItemsCarousel_user
                ...PdpRespSellerItemsCarousel_user
                ...PdpRespSimilarItemsLanding_user
                ...PdpRespSidebarButtons_user
                ...PdpRespSidebarBlurbs_user
                ...PdpRespSharedHeroCarousel_user
                ...PdpRespIconBubblesWrapper_user
                ...PdpRespSidebarSellerInfoAndContactLazy_user
                ...PdpSharedPromoCode_user
                ...PdpRespSwatchSelector_user @include(if: $isMultiSku)
                ...PdpRespDetailTabs_user
                ...PdpRespSharedPrintDetailsLazy_user
                ...PdpRespSimilarAuctionItemsLazy_user @include(if: $fetchAuction)
            }
        `,
        item: graphql`
            fragment PdpRespContainer_item on Item {
                firstPhotoWebPath(size: small)
                isAuctionEnabled
                itemIsOnHold: isOnHold(buyerId: $userId)
                ...PdpSharedEditorialArticles_item
                ...PdpRespSimilarItemsLanding_item
                ...PdpRespBreadcrumbs_item
                ...PdpRespTitle_item
                ...PdpRespSidebarPillsLazy_item
                ...PdpRespSidebarButtons_item
                ...PdpRespSidebarBlurbs_item
                ...PdpRespYMALItemsCarousel_item
                ...PdpRespSimilarItemsCarousel_item
                ...PdpRespSellerItemsCarousel_item
                ...PdpSharedQuantitySelect_item
                ...PdpSharedRecentHistoryTrackerDeprecated_item
                ...SharedRespLinksBlock_item
                ...PriceDisplay_item
                    @include(if: $isClient)
                    @arguments(
                        userId: $userId
                        isTrade: $isTrade
                        userCountryCode: $userCountryCode
                        priceBookName: $priceBookName
                    )
                ...PdpRespSwatchSelector_item @include(if: $isMultiSku)
                ...PdpSharedFramingStatus_item @include(if: $isClient)
                ...PdpRespDetailTabs_item
                ...pricingGuidanceAbHoc_item
                ...PdpRespSharedPrintDetailsLazy_item
                ...PdpSharedSidebarFavorites_item
                ...PdpSharedAuctionInfo_item @include(if: $fetchAuction)
                ...PdpSharedAuctionCountdownLazy_item
                ... @include(if: $fetchPdp) {
                    seller {
                        isAccessible
                        ...PdpRespSidebarSellerInfoAndContactLazy_seller
                    }
                    ...PdpRespSharedHeroCarousel_item
                    ...PdpRespIconBubblesWrapper_item
                    internalBuyLinks {
                        ...SharedRespLinksBlock_links
                    }
                }
                ...PdpRespSidebarSellerInfoAndContactLazy_item
                skus {
                    serviceId
                    ...PriceDisplay_sku
                        @include(if: $isMultiSku)
                        @arguments(
                            hasSkuId: true
                            userId: $userId
                            isTrade: $isTrade
                            userCountryCode: $userCountryCode
                            priceBookName: $priceBookName
                        )
                    ...PdpRespSidebarBlurbs_sku
                }
                storyTellingModule {
                    tabs {
                        supplementaryEditorial {
                            articles {
                                __typename
                            }
                        }
                    }
                }
            }
        `,
        displayInfo: graphql`
            fragment PdpRespContainer_displayInfo on ItemDisplayInfo {
                ctas {
                    showAcceptOffer
                }
                ...PdpRespSidebarButtons_displayInfo
                ...PdpSharedPromoCode_displayInfo
                ...PdpRespSidebarBlurbs_displayInfo
            }
        `,
        similarLandingItems: graphql`
            fragment PdpRespContainer_similarLandingItems on moreLikeThisItemConnection {
                ...PdpRespSimilarItemsLanding_similarItems
            }
        `,
        itemSearch: graphql`
            fragment PdpRespContainer_itemSearch on ItemSearchQueryConnection {
                ...PdpRespSidebarButtons_itemSearch
            }
        `,
    }
);
